import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { get, upperFirst } from 'lodash';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import WebMercatorViewport from '@math.gl/web-mercator';
import DeckGL, { IconLayer, ScatterplotLayer } from 'deck.gl';
import { Map as ReactMapGL } from 'react-map-gl/maplibre';
import { computeDestinationPoint } from 'geolib';

import { Section, EditButton } from 'components/DeliveryManager';

import CAR_MARKERS from 'assets/svg/car.svg';
import MAKRERS from 'assets/svg/markers.svg';
import HOME_MARKER from 'assets/svg/home_marker.svg';
import PARKING_MARKER from 'assets/svg/Parking_Marker.svg';
import ENTRANCE_MARKER from 'assets/svg/Entrance_Marker.svg';
import useSearchParams from 'hooks/useSearchParams';
import {
  ADDRESS,
  ADDRESS_NOT_ACCESSIBLE,
  COMMERCIAL_BUILDING,
  APARTMENT_COMPLEX,
  DRIVER_LOCATION_PIN_REFRESH_INTERVAL,
  DRIVER_LOCATION_RADIUS_REFRESH_INTERVAL,
} from 'constants/common';

const PREFIX = 'Location';

const classes = {
  container: `${PREFIX}-container`,
  map: `${PREFIX}-map`,
  description1: `${PREFIX}-description1`,
  notes: `${PREFIX}-notes`,
  note: `${PREFIX}-note`,
  image: `${PREFIX}-image`,
  text: `${PREFIX}-text`,
  description2: `${PREFIX}-description2`,
  notice: `${PREFIX}-notice`,
  highlight: `${PREFIX}-highlight`,
};

const StyledSection = styled(Section)(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: '1rem',
    marginTop: '1rem',
    [theme.breakpoints.up('sm')]: {
      gridAutoFlow: 'column',
      gridTemplateColumns: '1fr 1fr',
    },
  },

  [`& .${classes.map}`]: {
    width: '100%',
    height: '350px',
    order: 1,
    [theme.breakpoints.up('sm')]: {
      order: 2,
    },
  },

  [`& .${classes.description1}`]: {
    color: theme.colorTokens.onSurfaceTextLabel,
    fontSize: 14,
  },

  [`& .${classes.notes}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    order: 2,
    [theme.breakpoints.up('sm')]: {
      order: 1,
    },
  },

  [`& .${classes.note}`]: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: theme.colorTokens.surfaceDim,
    alignItems: 'center',
    height: '50px',
  },

  [`& .${classes.image}`]: {
    width: '32px',
    height: '40px',
    objectFit: 'contain',
  },

  [`& .${classes.text}`]: {
    color: theme.colors.common1,
    fontSize: '12px',
    margin: 0,
  },

  [`& .${classes.description2}`]: {
    color: theme.colorTokens.onSurfaceTextHeadline,
  },

  [`& .${classes.notice}`]: {
    fontSize: '13px',
    color: theme.palette.warning.main,
    fontStyle: 'italic',
  },

  [`&.${classes.highlight}`]: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
}));

const GEOCODE = 'GEOCODE';
const PARKING = 'PARKING';
const SHIPMENT = 'SHIPMENT';
const GATE = 'GATE';
const CURRENT_LOCATION = 'CURRENT_LOCATION';

const ICON_MAPPING = {
  [GEOCODE]: { x: 0, y: 0, width: 168, height: 224, anchorY: 210 },
  [PARKING]: { x: 0, y: 0, width: 195, height: 242, anchorY: 210 },
  [GATE]: { x: 0, y: 0, width: 195, height: 242, anchorY: 210 },
  [SHIPMENT]: { x: 690, y: 220, width: 60, height: 60 },
  [CURRENT_LOCATION]: { x: 530, y: 220, width: 60, height: 60 },
};

const MAP_STYLE = `https://api.maptiler.com/maps/topo/style.json?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`;

const NOTES = [
  { type: GEOCODE, icon: HOME_MARKER, text: 'Address Pin', description: 'a location pin of your address' },
  { type: GATE, icon: ENTRANCE_MARKER, text: 'Entrance Pin', description: 'a specific entrance pin to your address, if applicable' },
  { type: PARKING, icon: PARKING_MARKER, text: 'Parking Pin', description: 'a convenient parking location for drivers when delivering your package' },
];

const INITIAL_VIEW_STATE = {
  latitude: 37.772402,
  longitude: -122.414265,
  zoom: 16,
  width: 400,
  height: 400,
  maxZoom: 20,
  minZoom: 5,
};

export const Location = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewport, setViewport] = useState(INITIAL_VIEW_STATE);
  const [isModifiedViewState, setIsModifiedViewState] = useState(false);

  const { deliveryStore } = store;
  const { delivery, driverLocationDetail } = deliveryStore;
  const locations = delivery?.['customer-locations'];
  // no-dd-sa:javascript-code-style/assignment-name
  const dropoff_address = delivery?.shipment?.dropoff_address;
  const addressType = get(delivery, 'recipient-questionnaire.address_type');

  const isFar = driverLocationDetail?.is_driver_far;
  const driverLocation = driverLocationDetail?.driver_location;
  const distanceInMeters = driverLocationDetail?.driver_distance ? Number(driverLocationDetail.driver_distance) : 0;

  const reason = searchParams.get('reason');
  const highlight = reason === ADDRESS_NOT_ACCESSIBLE;

  const dropoff = { latitude: dropoff_address.lat, longitude: dropoff_address.lng, type: SHIPMENT };
  const geocode = (locations || []).find((address) => address.type === GEOCODE);
  const parking = (locations || []).find((address) => address.type === PARKING);
  const gate = (locations || []).find((address) => address.type === GATE);
  const pins = [
    { ...dropoff, id: SHIPMENT, icon: MAKRERS, size: 24 },
    { ...geocode, id: GEOCODE, icon: HOME_MARKER },
  ];

  if (parking) pins.push({ ...parking, id: PARKING, icon: PARKING_MARKER });
  if (gate) pins.push({ ...gate, id: GATE, icon: ENTRANCE_MARKER });

  const handleOpenDialog = () => {
    searchParams.delete('dialog');
    searchParams.set('reason', ADDRESS);
    setSearchParams(searchParams);
  };

  const renderMarker = ({ id, latitude, longitude, icon, size }) => {
    return new IconLayer({
      id: id,
      data: [{ icon: id, location: [longitude, latitude], size: size || 40 }],
      pickable: true,
      iconAtlas: icon,
      iconMapping: ICON_MAPPING,
      getIcon: (d) => d.icon,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: true,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
    });
  };

  const renderDriverLocation = () => {
    if (driverLocation) {
      const iconData = [{ size: 20, location: [driverLocation.longitude, driverLocation.latitude] }];
      const iconMapping = { white: { x: 0, y: 50, width: 100, height: 50, anchorY: 25 } };
      const angle = driverLocation.heading ? (driverLocation.heading < 90 ? 90 - driverLocation.heading : 450 - driverLocation.heading) : 90;

      return new IconLayer({
        id: 'driver-location',
        data: iconData,
        pickable: false,
        iconAtlas: CAR_MARKERS,
        iconMapping: iconMapping,
        getIcon: () => 'white',
        sizeScale: 1,
        opacity: 1,
        visible: true,
        billboard: false,
        getPosition: (data) => data.location,
        getSize: (data) => data.size,
        getAngle: () => angle,
      });
    }

    if (isFar === false && distanceInMeters > 0 && dropoff_address) {
      return new ScatterplotLayer({
        id: 'driver-location-radius',
        data: [{ coordinates: [dropoff_address.lng, dropoff_address.lat] }],
        pickable: false,
        opacity: 0.1,
        stroked: true,
        filled: true,
        radiusMinPixels: 1,
        lineWidthMinPixels: 1,
        getPosition: (d) => d.coordinates,
        getRadius: distanceInMeters,
        getFillColor: () => [74, 158, 255],
        getLineColor: () => [0, 0, 0],
      });
    }
  };

  const fitMap = useCallback(
    (from, to) => {
      if (isModifiedViewState) return;

      const dx = Math.max(Math.abs(from.latitude - to.latitude), 0.002);
      const dy = Math.max(Math.abs(from.longitude - to.longitude), 0.002);

      if (!viewport.width) return;

      const wmv = new WebMercatorViewport(viewport);
      const { longitude, latitude, zoom } = wmv.fitBounds(
        [
          [from.longitude - dy, from.latitude - dx],
          [from.longitude + dy, from.latitude + dx],
        ],
        { padding: 100 },
      );

      setViewport({
        ...viewport,
        longitude,
        latitude,
        zoom,
        transitionDuration: 200,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dropoff_address, driverLocation, isModifiedViewState],
  );

  const handleViewStateChange = (viewStateChange) => {
    const { viewState, oldViewState } = viewStateChange;

    setViewport(viewState);
    if (oldViewState.zoom && viewState.zoom !== oldViewState.zoom && isModifiedViewState === false) setIsModifiedViewState(true);
  };

  const renderNotes = () => {
    if (reason === ADDRESS_NOT_ACCESSIBLE)
      return <p className={classes.notice}>*Address Not Accessible - Please help us to locate your address better through location pins feature.</p>;
    if ([APARTMENT_COMPLEX, COMMERCIAL_BUILDING].includes(addressType))
      return (
        <p className={classes.notice}>
          *For apartment complex, pins can be very useful for our drivers to find your correct address in the shortest time.
        </p>
      );
  };

  const driverLocationLayer = renderDriverLocation();
  const dropoffLocationLayers = pins.map((item) => renderMarker(item));
  const layers = [driverLocationLayer, ...dropoffLocationLayers].filter(Boolean);
  const visiblePinTypes = pins.map((p) => p.id);
  const visiableNotes = NOTES.filter((note) => visiblePinTypes.includes(note.type));
  const isValidViewport = viewport.latitude !== undefined && viewport.longitude !== undefined;

  useEffect(() => {
    if (dropoff_address?.lat == null || dropoff_address?.lng == null) return;

    setViewport((current) => ({ ...current, longitude: dropoff_address.lng, latitude: dropoff_address.lat }));
  }, [dropoff_address]);

  useEffect(() => {
    if (dropoff_address?.lat == null || dropoff_address?.lng == null) return;

    const from = { longitude: dropoff_address.lng, latitude: dropoff_address.lat };
    const to = driverLocation || distanceInMeters ? computeDestinationPoint(from, Number(distanceInMeters), 0) : null;

    if (from && to) fitMap(from, to);
  }, [dropoff_address, distanceInMeters, driverLocation, fitMap]);

  useEffect(() => {
    const shouldRefresh = isFar === false || driverLocation;

    if (!shouldRefresh) return;

    const interval = isFar ? DRIVER_LOCATION_PIN_REFRESH_INTERVAL : DRIVER_LOCATION_RADIUS_REFRESH_INTERVAL;

    const timerId = setInterval(() => {
      deliveryStore.getDriverLocation();
    }, interval);

    return () => {
      clearInterval(timerId);
    };
  }, [deliveryStore, driverLocation, isFar]);

  return (
    <StyledSection className={clsx({ [classes.highlight]: highlight })}>
      <div className="section__header">
        <span className="section__title">Location Pins:</span>
        <EditButton size="small" variant="outlined" color="primary" onClick={handleOpenDialog} data-testid="location-pin-edit-button">
          Edit
        </EditButton>
      </div>
      <div className={clsx({ section__body: true, [classes.container]: true })}>
        <div className={classes.map}>
          {isValidViewport && (
            <ReactMapGL
              {...viewport}
              mapStyle={MAP_STYLE}
              onMove={(evt) => handleViewStateChange({ viewState: evt.viewState, oldViewState: viewport })}
              style={{ width: '100%', height: '100%' }}
              attributionControl={false}
            >
              <DeckGL layers={layers} viewState={viewport} style={{ pointerEvents: 'none' }} />
            </ReactMapGL>
          )}
        </div>
        <div className={classes.notes}>
          <p className={classes.description1}>Provide us some helpful pins to assist your delivery</p>
          {visiableNotes.map((note) => (
            <div className={classes.note} key={note.type}>
              <img src={note.icon} alt="" className={classes.image} />
              <div>
                <p className={classes.text}>
                  <span style={{ fontWeight: 700, display: 'block' }}>{note.text}</span>
                  <span className={classes.description2}>{upperFirst(note.description)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {renderNotes()}
    </StyledSection>
  );
});
